import React from 'react';
import { X } from 'lucide-react';

interface CaseExample {
  id: string;
  title: string;
  description: string;
}

const CASE_EXAMPLES: CaseExample[] = [
  {
    id: '1',
    title: 'Class II - Maxillary Distalization >1mm',
    description: 'The patient presents with a Class II malocclusion and has all permanent teeth present. There is crowding observed in both the upper and lower anterior regions. Additionally, the patient exhibits maxillary transverse constriction in the premolar region and a pronounced Spee curve on the lower arch. The upper midline is aligned with the facial midline. Based on the patient profile, maxillary distalization is indicated, with a requirement to distalize more than 1mm.'
  },
  {
    id: '2',
    title: 'Class II - Maxillary Distalization ≤1mm',
    description: 'The patient presents with a Class II malocclusion and has all permanent teeth present. There is crowding observed in both the upper and lower anterior regions. Additionally, the patient exhibits maxillary transverse constriction in the premolar region and a pronounced Spee curve on the lower arch. The upper midline is aligned with the facial midline. Based on the patient profile, maxillary distalization is indicated, with a requirement to distalize 1mm or less.'
  },
  {
    id: '3',
    title: 'Class II - Mandibular Mesialization',
    description: 'The patient presents with a Class II malocclusion and has all permanent teeth present. There is crowding observed in both the upper and lower anterior regions. Additionally, the patient exhibits maxillary transverse constriction in the premolar region and a pronounced Spee curve on the lower arch. Based on the patient profile, mandibular mesialization is indicated.'
  }
];

interface CaseSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectCase: (caseDescription: string) => void;
}

const CaseSelectionModal: React.FC<CaseSelectionModalProps> = ({
  isOpen,
  onClose,
  onSelectCase
}) => {
  if (!isOpen) return null;

  const handleCaseSelect = (caseExample: CaseExample) => {
    onSelectCase(caseExample.description);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[80vh] overflow-hidden shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-medical-200">
          <h2 className="text-xl font-semibold text-medical-900">Select a Case Example</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-medical-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-medical-600" />
          </button>
        </div>

        {/* Case List */}
        <div className="p-6 overflow-y-auto max-h-[calc(80vh-80px)]">
          <p className="text-medical-600 mb-6">
            Choose a case example below to start drafting a treatment plan:
          </p>
          
          <div className="space-y-4">
            {CASE_EXAMPLES.map((caseExample) => (
              <button
                key={caseExample.id}
                onClick={() => handleCaseSelect(caseExample)}
                className="w-full text-left p-4 border border-medical-200 rounded-lg hover:border-primary-500 hover:bg-primary-50 transition-all group"
              >
                <h3 className="font-semibold text-medical-900 mb-2 group-hover:text-primary-700">
                  {caseExample.title}
                </h3>
                <p className="text-sm text-medical-600 leading-relaxed">
                  {caseExample.description}
                </p>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CaseSelectionModal;